
import Vue, { PropType } from 'vue';
import { Timeline } from '@descomplica/vue-components';
import {
  Assessment,
  AssessmentResult,
  ASSESSMENT_RESULT_STATUS,
  Reply,
  REPLY_STATUS,
  REPLY_TYPE,
} from '@/models/greathall/assessment.model';
import PostGradTCCDevelopmentSubmission from './PostGradTCCDevelopmentSubmission.vue';
import PostGradTCCWaiting from './PostGradTCCWaiting.vue';
import PostGradTCCDevelopmentNextStep from './PostGradTCCDevelopmentNextStep.vue';
import { TabType, ThesisDevelopmentItem, ThesisDevelopmentItemType } from './types';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import Analytics from '@/helpers/analytics';

export default Vue.extend({
  components: {
    Timeline,
    PostGradTCCDevelopmentSubmission,
    PostGradTCCWaiting,
    PostGradTCCDevelopmentNextStep,
  },
  props: {
    assessment: {
      type: Object as PropType<Assessment>,
      required: true,
    },
    dexCourseId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      showOptionalSubmission: false,
    };
  },
  computed: {
    thesisDevelopmentItems(): Array<ThesisDevelopmentItem> {
      const thesisDevelopmentReplies = this.assessmentResult.replies.filter(
        (reply: Reply) => reply.type === REPLY_TYPE.PARTIAL_TCC,
      );

      const submitedFinalTCC = this.assessmentResult.replies.some(
        (reply: Reply) => reply.type === REPLY_TYPE.FINAL_TCC,
      );

      const hasPartialTCCApproved = thesisDevelopmentReplies.some(
        (reply) => reply.status === REPLY_STATUS.APPROVED,
      );

      if (thesisDevelopmentReplies.length === 0) {
        return [
          {
            type: ThesisDevelopmentItemType.firstSubmission,
            enabled: true,
            data: {
              interactionsLeft: this.assessmentResult.maxPartialTCCSubmissions,
            },
            isLast: true,
          },
        ];
      }

      const sectionItems = thesisDevelopmentReplies.reduce<ThesisDevelopmentItem[]>(
        (developmentItems: ThesisDevelopmentItem[], reply: Reply, index: number) => {
          const interactionsLeft = this.assessmentResult.maxPartialTCCSubmissions - index - 1;
          const items: ThesisDevelopmentItem[] = [];
          const nextReply = thesisDevelopmentReplies[index + 1] || null;

          if (index === 0) {
            items.push({
              type: ThesisDevelopmentItemType.firstSubmission,
              enabled: false,
              data: {
                link: reply.link,
                interactionsLeft: this.assessmentResult.maxPartialTCCSubmissions,
              },
              isLast: false,
            });
          }

          if (reply.status === REPLY_STATUS.PENDING) {
            items.push({
              type: ThesisDevelopmentItemType.waitingReview,
              enabled: true,
              isLast: false,
            });
          } else if (reply.status === REPLY_STATUS.REJECTED) {
            items.push({
              type: ThesisDevelopmentItemType.adjustmentsRequested,
              enabled: !nextReply && !submitedFinalTCC,
              data: {
                feedbackText: reply.feedbackText,
                feedbackLink: reply.feedbackLink,
                link: nextReply?.link,
                interactionsLeft,
                nextStepAvailable: hasPartialTCCApproved,
              },
              isLast: false,
            });

            if (interactionsLeft === 0) {
              items.push({
                type: ThesisDevelopmentItemType.noInteractionRemaining,
                enabled: true,
                data: {},
                isLast: false,
              });
            }
          } else {
            items.push({
              type: ThesisDevelopmentItemType.developmentApproved,
              enabled: !nextReply,
              data: {
                feedbackText: reply.feedbackText,
                feedbackLink: reply.feedbackLink,
                interactionsLeft: submitedFinalTCC ? 0 : interactionsLeft,
              },
              isLast: false,
            });

            if (
              interactionsLeft > 0 &&
              !submitedFinalTCC &&
              (this.showOptionalSubmission || nextReply?.link)
            ) {
              items.push({
                type: ThesisDevelopmentItemType.optionalSubmission,
                enabled: !nextReply,
                data: {
                  interactionsLeft,
                  link: nextReply?.link,
                  nextStepAvailable: hasPartialTCCApproved,
                },
                isLast: false,
              });
            }
          }

          return developmentItems.concat(items);
        },
        [],
      );

      sectionItems[sectionItems.length - 1].isLast = true;

      return sectionItems;
    },
    assessmentResult(): AssessmentResult {
      return this.assessment.assessmentResultsByUser[0] || null;
    },
  },
  methods: {
    async sendPartialTCC(partialTCC: any) {
      const { file: dataUrl, fileName } = partialTCC;
      this.$emit('update:loading', true);

      await this.$store.dispatch('SEND_PARTIAL_TCC', {
        assessmentResultId: this.assessmentResult.id,
        dexCourseId: this.dexCourseId,
        fileName,
        dataUrl,
      });

      this.trackEvents();

      await this.$store.dispatch('PULL_ASSESSMENT_TCC_DATA', {
        dexCourseId: this.dexCourseId,
        assessmentId: this.assessment.id,
      });

      this.$emit('update:loading', false);
    },
    trackEvents(): void {
      const assessmentResults = this.assessment.assessmentResultsByUser;
      const relatedAssessmentCategory = this.assessment.category.name;

      const attemptNumber =
        assessmentResults.filter((ar) => ar.status === ASSESSMENT_RESULT_STATUS.FAILED).length + 1;

      const developmentReplies = this.assessmentResult.replies.filter(
        (reply: Reply) => reply.type === REPLY_TYPE.PARTIAL_TCC,
      );

      if (developmentReplies.length === 0)
        Analytics.trackEvent(EVENT_LABELS.tccOrientationStart, {
          attemptNumber,
          relatedAssessmentCategory,
        });

      Analytics.trackEvent(EVENT_LABELS.tccOrientation, {
        attemptNumber,
        relatedAssessmentCategory,
        interactionNumber: developmentReplies.length + 1,
        isOptional: developmentReplies.some((reply) => reply.status === REPLY_STATUS.APPROVED),
      });
    },
    goToFinalThesis() {
      this.$emit('selectTab', TabType.finalThesis);
    },
    openOptionalSubmission() {
      this.showOptionalSubmission = true;
    },
  },
});
